<script lang="ts" setup>
import AuthAPI from '~/api/auth';
import type { ResponseError } from '~/api/utils/types';

const TEST_ID = 'auth-phone-form';

defineProps<{
  withTitle?: boolean;
}>();
const emit = defineEmits<{
  (event: 'submit', value: { phone: string }): void;
  (event: 'to-email'): void;
}>();
const authorizationTheme = inject(
  INJECTION_KEYS.AuthorizationTheme,
  ThemeEnum.dark,
);

const { t } = useI18n();
const modalStore = useModalStore();

const phone = usePhoneInput();

const { execute, isLoading, isValid } = useRequest(
  AuthAPI.phoneAuth,
  () => ({ body: { phone: phone.value, flash: true } }),
  { validation: () => checkPhoneIsValid(phone.value) },
);

const onSubmit = () =>
  execute()
    ?.then(() => {
      emit('submit', { phone: phone.value });
    })
    .catch((e: ResponseError) => {
      modalStore.showError({
        title: getFetchErrorMessage(e, t('message.error')),
        action: t('message.return'),
      });
    });
</script>

<template>
  <form @submit.prevent="onSubmit" :data-test-id="TEST_ID">
    <h2 v-if="withTitle" class="authorizationComponent__title">
      {{ t('title') }}
    </h2>
    <div class="authorizationComponent__fields">
      <BaseTextField
        :placeholder="t('phone_placeholder')"
        :bordered="authorizationTheme === ThemeEnum.light"
        class="authorizationComponent__input"
        v-bind="phone.inputProps"
        :data-test-id="`${TEST_ID}-input`"
      />
    </div>
    <span class="authorizationComponent__hint">{{ t('hint') }}</span>
    <button
      @click="emit('to-email')"
      class="authorizationComponent__link"
      type="button"
      :data-test-id="`${TEST_ID}-to-email`"
    >
      {{ t('to_email') }}
    </button>

    <BaseButton
      :disabled="!isValid"
      :loading="isLoading"
      type="submit"
      class="authorizationComponent__submit"
      :dark="authorizationTheme === ThemeEnum.light"
      :data-test-id="`${TEST_ID}-submit`"
    >
      {{ t('submit') }}
    </BaseButton>
  </form>
</template>

<i18n>
ru:
  title: Войти или зарегистрироваться
  phone_placeholder: Телефон
  hint: Номер телефона должен быть привязан к вашей учетной записи в личном кабинете.
  to_email: Войти по почте
  submit: Войти
  message:
    error: При запросе СМС кода произошла ошибка
    return: Вернуться

en:
  title: Sign in or register
  phone_placeholder: Phone
  hint: The phone number must be linked to your account in your personal account.
  to_email: Sign in by email
  submit: Sign in
  message:
    error: An error occurred while requesting an SMS code
    return: Return
</i18n>
